import { css } from "@emotion/css";
import { List, ListItem } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { GitHubAppConnectionSummary, GitHubAppConnections, GitHubAppInstallation, GitHubAppInstallations } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import InternalLink from "~/components/Navigation/InternalLink";
import { GitHubConnectionStatus } from "./ChipList/GitHubChips";
import GitHubAppInstallationDisplay from "./GitHubAppInstallationDisplay";
import { GitHubConnectionRepositorySelector } from "./RepositorySelector/GitHubConnectionRepositorySelector";
interface GitHubAppConnectionListProps {
    spaceId: string;
    connections: GitHubAppConnections;
    notConnectedInstallations: GitHubAppInstallations | undefined;
}
export default function GitHubAppConnectionList({ spaceId, connections, notConnectedInstallations }: GitHubAppConnectionListProps) {
    const items = [];
    for (const connection of connections.Connections) {
        items.push({
            id: "connection-" + connection.Id,
            listItem: <GitHubAppConnectionListItem spaceId={spaceId} connection={connection}/>,
        });
    }
    if (notConnectedInstallations) {
        for (const installation of notConnectedInstallations.Installations) {
            items.push({
                id: "installation-" + installation.InstallationId,
                listItem: <GitHubAppInstallationListItem spaceId={spaceId} installation={installation}/>,
            });
        }
    }
    return <List items={items} rowKey={(i) => i.id} renderRow={(props) => props.item.listItem}/>;
}
interface GitHubAppConnectionListItemProps {
    spaceId: string;
    connection: GitHubAppConnectionSummary;
}
function GitHubAppConnectionListItem({ spaceId, connection }: GitHubAppConnectionListItemProps) {
    const link = links.gitHubConnectionPage.generateUrl({ spaceId: spaceId, connectionId: connection.Id });
    return (<ListItem>
            <InternalLink to={link}>
                {connection.Installation ? <GitHubAppInstallationDisplay installation={connection.Installation} connectionStatus={GitHubConnectionStatus.Connected}/> : <GitHubAppConnectionMissingDetails connectionId={connection.Id}/>}
            </InternalLink>
        </ListItem>);
}
interface GitHubAppInstallationListItemProps {
    spaceId: string;
    installation: GitHubAppInstallation;
}
function GitHubAppInstallationListItem({ spaceId, installation }: GitHubAppInstallationListItemProps) {
    const useRepositorySelectionModal = installation.AllRepositories;
    if (useRepositorySelectionModal) {
        return <GitHubAppInstallationListItemForModal spaceId={spaceId} installation={installation}/>;
    }
    const link = links.addGitHubConnectionPage.generateUrl({ spaceId, installationId: installation.InstallationId });
    return (<ListItem>
            <InternalLink to={link}>
                <GitHubAppInstallationDisplay installation={installation} connectionStatus={GitHubConnectionStatus.Available}/>
            </InternalLink>
        </ListItem>);
}
function GitHubAppInstallationListItemForModal({ spaceId, installation }: GitHubAppInstallationListItemProps) {
    const dialog = useDialogTrigger();
    const styles = {
        button: css({
            display: "block",
            width: "100%",
            border: "none",
            background: "none",
            textAlign: "inherit",
            padding: 0,
            margin: 0,
            cursor: "pointer",
        }),
    };
    return (<ListItem>
            <button className={styles.button} onClick={() => dialog.openDialog()}>
                <GitHubAppInstallationDisplay installation={installation} connectionStatus={GitHubConnectionStatus.Available}/>
            </button>
            <GitHubConnectionRepositorySelector spaceId={spaceId} isOpen={dialog.isOpen} closeDialog={dialog.closeAllDialogs} installation={installation}/>
        </ListItem>);
}
interface GitHubAppConnectionMissingDetailsProps {
    connectionId: string;
}
function GitHubAppConnectionMissingDetails({ connectionId }: GitHubAppConnectionMissingDetailsProps) {
    const styles = {
        container: css({
            display: "flex",
            flexDirection: "row",
            padding: space[16],
            gap: space[16],
            alignItems: "center",
        }),
    };
    return (<div className={styles.container}>
            <h3>{connectionId}</h3>
        </div>);
}
